import { DomainError } from '@/core/errors/DomainError';

import type {
  FormatSpec,
  PhaseStateContext,
  PlanContext,
  ProgressionContext,
  StandingsContext,
  TournamentFormat,
} from './contracts';

/**
 * Wraps a typed format into the erased {@link TournamentFormat} interface.
 *
 * The configuration is parsed once at the entry of every method, so a format
 * implementation never sees an unvalidated value and callers never carry a type
 * parameter around. A malformed configuration surfaces as a domain error naming
 * the format, not as an unexplained crash deep inside a bracket calculation.
 */
export function defineFormat<TConfig>(spec: FormatSpec<TConfig>): TournamentFormat {
  const parseConfig = (input: unknown): TConfig => {
    const result = spec.configSchema.safeParse(input);

    if (!result.success) {
      const details = result.error.issues
        .map((issue) => `${issue.path.join('.') || '(Wurzel)'}: ${issue.message}`)
        .join('; ');

      throw new DomainError(
        'INVALID_PHASE_CONFIG',
        `Die Konfiguration für "${spec.label}" ist ungültig — ${details}`,
        { formatId: spec.id },
      );
    }

    return result.data;
  };

  return {
    id: spec.id,
    label: spec.label,
    parseConfig,

    createPlan: (context: PlanContext) =>
      spec.createPlan({ ...context, config: parseConfig(context.config) }),

    computeStandings: (context: StandingsContext) =>
      spec.computeStandings({ ...context, config: parseConfig(context.config) }),

    resolveProgression: (context: ProgressionContext) =>
      spec.resolveProgression({ ...context, config: parseConfig(context.config) }),

    selectQualifiers: (context: StandingsContext) =>
      spec.selectQualifiers({ ...context, config: parseConfig(context.config) }),

    isComplete: (context: PhaseStateContext) =>
      spec.isComplete({ ...context, config: parseConfig(context.config) }),
  };
}
